import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { BasicStep } from "../components/basic-step";
import styles from "./index.module.css";
import i18nPackage from "../../i18n/i18n";
import { Divider, Link, SvgIcon, Tooltip, Typography } from "@mui/material";
import { VerifyIdentityKey, VerifyIdentityKeyNoReview } from "../before-quiz";
import {
	BaseSubstepProps,
	BasicSubstep,
	BasicSubstepDescription,
	BasicSubstepHeader,
	BasicSubstepTitle,
	getBasicSubstepIcon,
	SubstepStatus,
} from "../components/basic-substep";
import {
	BeforeQuizProgressBar,
	CameraPreview,
	CameraPreviewProps,
	DeviceDropdown,
	DeviceDropdownProps,
	getCameraDescriptionError,
	getCameraTitleError,
} from "../components/media-components";
import { CoreButton, CoreTextField } from "../../core";
import { Trans } from "react-i18next";
import CheckmarkIcon from "../../images/checkmark.svg?react";
import RetakeIcon from "../../images/retake-circle.svg?react";
import SignaturePad from "signature_pad";
import ClearIcon from "../../images/x.svg?react";
import Warning from "../../images/exclamation-filled.svg?react";
import Signature from "../../images/signature.svg?react";
import AiFailed from "../../images/ai-fail-shield.svg?react";

type StepProgressLineProps = {
	steps: Record<VerifyIdentityKey, { active: boolean; complete: boolean }>;
};

export function StepProgressLine({ steps }: StepProgressLineProps) {
	const keys = Object.keys(steps) as VerifyIdentityKey[];
	return (
		<div className={styles.stepProgressLineContainer} style={{ gridTemplateColumns: `repeat(${keys.length}, 1fr)` }}>
			{keys.map((step, i) => (
				<StepSegment
					key={i}
					label={i18nPackage.t(`beforeQuiz.verifyIdentity.substep.${step}`)}
					active={steps[step].active}
					complete={steps[step].complete}
				/>
			))}
		</div>
	);
}

type StepSegmentProps = {
	label: string;
	active: boolean;
	complete: boolean;
};

// NO IDEA WHAT TO NAME BECAUSE THERE ARE A LOT OF STEPPERS
function StepSegment({ label, active, complete }: StepSegmentProps) {
	return (
		<div className={`${styles.stepSegmentContainer} ${active || complete ? styles.active : ""}`}>
			<div className={styles.stepSegmentBar} />
			<div className={styles.labelContainer}>
				{complete && <CheckmarkIcon />}
				<Typography className={styles.segmentLabel}>{label}</Typography>
			</div>
		</div>
	);
}

type IdVerificationSubstepProps = BaseSubstepProps & {
	type: Extract<VerifyIdentityKey, "idCard" | "faceVerification">;
	onTakePhoto: () => Promise<void>;
	slotProps: {
		cameraPreview: CameraPreviewProps;
		cameraDropdown: Omit<DeviceDropdownProps, "type">;
	};
};

export function IdVerificationSubstep({ status, type, onTakePhoto, slotProps }: IdVerificationSubstepProps) {
	const { devices, onChange, selectedDeviceIds, container: dropdownContainer } = slotProps.cameraDropdown;
	const { cameraError, isVideoLoading } = slotProps.cameraPreview;

	const [flash, setFlash] = useState(false);

	const handleTakePhoto = () => {
		setFlash(true);
		setTimeout(async () => {
			setFlash(false);
			await onTakePhoto();
		}, 150);
	};

	const title = cameraError
		? getCameraTitleError(cameraError)
		: i18nPackage.t(`beforeQuiz.verifyIdentity.${type}.title`);

	const description = cameraError ? (
		getCameraDescriptionError(cameraError)
	) : (
		<Trans
			i18nKey={`beforeQuiz.verifyIdentity.${type}.description`}
			i18n={i18nPackage}
			components={{ b: <strong /> }}
		/>
	);

	const buttonText = i18nPackage.t(`beforeQuiz.verifyIdentity.${type}.button`);

	return (
		<BasicSubstep className={styles.idVerificationWrapper}>
			{getBasicSubstepIcon(status)}
			<div className={styles.idVerificationLeftRegion}>
				<BasicSubstepTitle title={title} />
				<BasicSubstepDescription description={description} className={styles.idVerificationDescription} />
				{(status === "failed" || status === "unstarted") && (
					<DeviceDropdown
						devices={devices}
						onChange={onChange}
						selectedDeviceIds={selectedDeviceIds}
						container={dropdownContainer}
						type="camera"
						disabled={isVideoLoading && !cameraError}
					/>
				)}
			</div>
			<div className={styles.idVerificationRightRegion}>
				<div className={styles.cameraOverlayWrapper}>
					<div className={`${styles.flashOverlay} ${flash ? styles.active : ""}`} />
					<CameraPreview {...slotProps.cameraPreview} />

					{type === "idCard" && !isVideoLoading && !flash && (
						<>
							<div className={`${styles.darkCanvas} ${styles.id}`} />
							<div className={styles.idCardOverlayContainer}>
								<div className={styles.idTopLeftCorner} />
								<div className={styles.idTopRightCorner} />
								<div className={styles.idBottomRightCorner} />
								<div className={styles.idBottomLeftCorner} />
							</div>
						</>
					)}
					{type === "faceVerification" && !isVideoLoading && !flash && (
						<>
							<div className={`${styles.darkCanvas} ${styles.face}`} />
							<div className={styles.faceOverlay} />
						</>
					)}
				</div>
				<CoreButton onClick={handleTakePhoto} className={styles.takePhoto} disabled={!!cameraError || isVideoLoading}>
					{buttonText}
				</CoreButton>
			</div>
		</BasicSubstep>
	);
}

export type SignatureType = "draw" | "type";

type SignatureSubstepProps = BaseSubstepProps & {
	type: SignatureType;
	onSwitchMethod: () => void;
	/**Returns if canvas is empty or text field is empty depending on signature type */
	onSignatureChanged?: (isEmpty: boolean) => void;
};

// Tried to keep all UI here so other places dont need to import packages
// But since all UI is here, parent needs to know whats the signature
export interface SignatureSubstepRef {
	getSignature: () => string;
}

export const SignatureSubstep = forwardRef(function SignatureSubstep(
	{ type, onSwitchMethod, onSignatureChanged }: SignatureSubstepProps,
	ref: ForwardedRef<SignatureSubstepRef>,
) {
	const [typedSignature, setTypedSignature] = useState<string>("");
	const [sigCanvasEmpty, setSigCanvasEmpty] = useState<boolean>(true);
	const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const signaturePadRef = useRef<SignaturePad | null>(null);
	const resizeCanvasRef = useRef<() => void>();
	const containerRef = useRef<HTMLDivElement | null>(null);

	const initSignaturePadCanvas = useCallback((el: HTMLCanvasElement | null) => {
		if (el) {
			signatureCanvasRef.current = el;

			signaturePadRef.current = new SignaturePad(el);
			signaturePadRef.current.addEventListener("beginStroke", () => setSigCanvasEmpty(false));

			const resizeCanvas = () => {
				if (!el.parentElement) return;

				const ratio = Math.max(window.devicePixelRatio || 1, 1);
				const parentWidth = el.parentElement.clientWidth;
				const parentHeight = el.parentElement.clientHeight;

				el.width = parentWidth * ratio;
				el.height = parentHeight * ratio;

				el.style.width = `${parentWidth}px`;
				el.style.height = `${parentHeight}px`;

				const ctx = el.getContext("2d");
				ctx?.scale(ratio, ratio);

				signaturePadRef.current?.clear();
			};

			resizeCanvasRef.current = resizeCanvas;
			window.addEventListener("resize", resizeCanvas);
			resizeCanvas();
		} else {
			if (resizeCanvasRef.current) {
				window.removeEventListener("resize", resizeCanvasRef.current);
			}
			resizeCanvasRef.current = undefined;
		}
	}, []);

	const clearCanvas = () => {
		setSigCanvasEmpty(true);
		signaturePadRef.current?.clear();
	};

	useImperativeHandle(
		ref,
		() => ({
			getSignature: () => {
				if (type === "draw") {
					const canvas = signatureCanvasRef.current;
					if (!canvas) throw new Error("Signature canvas not initialized");
					return canvas.toDataURL("image/png");
				}

				return typedSignature;
			},
		}),
		[typedSignature, type],
	);

	useEffect(() => {
		if (type === "type") {
			onSignatureChanged?.(typedSignature.trim() === "");
		} else {
			onSignatureChanged?.(sigCanvasEmpty);
		}
	}, [typedSignature, type, onSignatureChanged, sigCanvasEmpty]);

	const alternateKey =
		type === "draw"
			? "beforeQuiz.verifyIdentity.signature.preferTyping"
			: "beforeQuiz.verifyIdentity.signature.preferDraw";
	return (
		<BasicSubstep className={styles.signatureContainer}>
			{getBasicSubstepIcon("unstarted")}
			<div className={styles.signatureLeftRegion} ref={containerRef} tabIndex={-1}>
				<BasicSubstepTitle title={i18nPackage.t(`beforeQuiz.verifyIdentity.signature.title.${type}`)} />
				<BasicSubstepDescription
					description={i18nPackage.t(`beforeQuiz.verifyIdentity.signature.description.${type}`)}
				/>
				{type === "type" && (
					<CoreTextField
						size="small"
						label={i18nPackage.t("beforeQuiz.verifyIdentity.signature.signaturePlaceholder")}
						onChange={(e) => setTypedSignature(e.target.value)}
						value={typedSignature}
						className={styles.typedSignatureTextField}
						alwaysShowLength
						maxLength={75}
					/>
				)}
				<Typography component={"span"} className={styles.switchMethod}>
					<Trans
						i18nKey={alternateKey}
						i18n={i18nPackage}
						components={{
							a: (
								<Link
									onClick={() => {
										containerRef.current?.focus();
										onSwitchMethod();
									}}
									component="button"
								/>
							),
						}}
					/>
				</Typography>
			</div>
			{type === "draw" && (
				<div className={styles.signatureRightRegion}>
					<div className={styles.drawContainer}>
						<Divider className={styles.signaturePlaceholderLine} />
						<canvas ref={initSignaturePadCanvas} />
						{sigCanvasEmpty && (
							<div className={styles.signatureOverlay}>
								<Signature />
								<Typography>{i18nPackage.t("beforeQuiz.verifyIdentity.signature.drawSignaturePlaceholder")}</Typography>
							</div>
						)}
					</div>
					<div className={styles.signatureControls}>
						<CoreButton
							disabled={sigCanvasEmpty}
							color="cancel"
							onClick={clearCanvas}
							startIcon={<ClearIcon width={18} height={18} />}
						>
							{i18nPackage.t("beforeQuiz.verifyIdentity.signature.clear")}
						</CoreButton>
					</div>
				</div>
			)}
		</BasicSubstep>
	);
});

function getReviewTitle({ isAiIDCheck, status }: { status: SubstepStatus; isAiIDCheck: boolean }) {
	if (isAiIDCheck) {
		if (status === "failed") {
			return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.title.aiFailed");
		} else if (status === "completed") {
			return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.title.aiPass");
		}
		return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.title.aiStarted");
	}
	return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.title.noAiTitle");
}

function getReviewDescription({
	isAiIDCheck,
	status,
	hasIdCheck,
	hasSignature,
}: {
	status: SubstepStatus;
	isAiIDCheck: boolean;
	hasSignature: boolean;
	hasIdCheck: boolean;
}) {
	if (isAiIDCheck) {
		if (status === "failed") {
			return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.description.failed");
		} else if (status === "completed") {
			return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.description.aiPass");
		} else if (hasSignature && hasIdCheck) {
			return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.description.aiStarted");
		}
		return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.description.aiStartedNoSig");
	} else if (hasSignature && hasIdCheck) {
		return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.description.manualAll");
	} else if (hasSignature) {
		return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.description.signatureOnly");
	}
	return i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.description.idOnly");
}

type ReviewPhotoItem = {
	url?: string;
	typedSignature?: boolean;
	onClick: () => void;
	key: VerifyIdentityKeyNoReview;
	/**Shows the failed symbol with a tooltip based on fail reason. */
	failReason?: VerifyIdentityFailReason;
};

type ReviewPhotosSubstepProps = BaseSubstepProps & {
	photos: ReviewPhotoItem[];
	isUploading: boolean;
	/** Showing different text for AI check */
	isAiCheck: boolean;
	/**So that tooltip doesnt appear outside shadow dom */
	containerRef?: HTMLElement;
};

export function ReviewPhotosSubstep({
	status,
	photos,
	isUploading,
	isAiCheck,
	containerRef,
}: ReviewPhotosSubstepProps) {
	const idIndex = photos.findIndex((p) => p.key === "idCard");
	const faceIndex = photos.findIndex((p) => p.key === "faceVerification");
	const signatureIndex = photos.findIndex((p) => p.key === "signature");
	const isAiFailed = isAiCheck && status === "failed";

	const shouldCombine = isAiFailed && idIndex > -1 && faceIndex > -1;

	const title = getReviewTitle({ isAiIDCheck: isAiCheck, status: status });
	const description = getReviewDescription({
		isAiIDCheck: isAiCheck,
		status,
		hasIdCheck: idIndex > -1 && faceIndex > -1,
		hasSignature: signatureIndex > -1,
	});

	return (
		<BasicSubstep className={styles.reviewSubstepContainer}>
			<BasicSubstepHeader
				status={status}
				title={title}
				description={description}
				icon={isAiFailed ? <AiFailed /> : undefined}
			/>
			<div className={styles.reviewPhotoGrid} style={{ gridTemplateColumns: `repeat(${photos.length}, max(300px))` }}>
				{photos.map((photo) => {
					return photo.url && !photo.typedSignature ? (
						<ReviewPhoto
							key={photo.key}
							url={photo.url}
							verifyIdentityKey={photo.key}
							failReason={photo.failReason}
							containerRef={containerRef}
						/>
					) : photo.typedSignature && photo.url ? (
						<TypedSignaturePhoto typedSignature={photo.url} />
					) : null;
				})}
				{!shouldCombine && !isUploading && (
					<>
						{photos.map((photo, index) => {
							return (
								<CoreButton
									className={styles.retakeButton}
									onClick={photo.onClick}
									startIcon={<RetakeIcon fill="#42296E" height={20} width={20} />}
									sx={{
										gridColumn: index === 0 ? "1" : undefined,
									}}
									key={photo.key}
									disabled={status === "started"}
								>
									{i18nPackage.t(`beforeQuiz.verifyIdentity.reviewPhoto.buttons.${photo.key}`)}
								</CoreButton>
							);
						})}
					</>
				)}

				{shouldCombine && (
					<>
						<CoreButton
							onClick={photos[faceIndex].onClick}
							className={styles.retakeButton}
							startIcon={<RetakeIcon fill="#42296E" height={20} width={20} />}
							sx={{ gridColumn: "span 2" }}
						>
							{i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.buttons.retryBoth")}
						</CoreButton>
						{signatureIndex > -1 && (
							<CoreButton
								className={styles.retakeButton}
								onClick={photos[signatureIndex].onClick}
								startIcon={<RetakeIcon fill="#42296E" height={20} width={20} />}
							>
								{i18nPackage.t("beforeQuiz.verifyIdentity.reviewPhoto.buttons.signature")}
							</CoreButton>
						)}
					</>
				)}

				{isUploading && <BeforeQuizProgressBar error={false} className={styles.identityProgress} />}
			</div>
		</BasicSubstep>
	);
}

export type VerifyIdentityFailReason = "upload" | "ai";

// Different UI for signature photo (border)
function ReviewPhoto({
	url,
	verifyIdentityKey,
	failReason,
	containerRef,
}: {
	url: string;
	verifyIdentityKey: VerifyIdentityKeyNoReview;
	failReason?: VerifyIdentityFailReason;
	containerRef?: HTMLElement;
}) {
	const failTooltip = failReason ? getFailedTooltip(verifyIdentityKey, failReason) : null;
	const popperProps = containerRef ? { container: containerRef } : {};

	return (
		<div className={`${styles.photoContainer} ${verifyIdentityKey === "signature" ? styles.signature : ""}`}>
			<img src={url} className={styles.photoImage} draggable={false} />
			{failTooltip && (
				<Tooltip title={failTooltip} PopperProps={popperProps}>
					<SvgIcon className={styles.cornerIcon}>
						<Warning />
					</SvgIcon>
				</Tooltip>
			)}
		</div>
	);
}

function getFailedTooltip(key: VerifyIdentityKeyNoReview, reason: VerifyIdentityFailReason) {
	if (reason === "upload") {
		return i18nPackage.t(`beforeQuiz.verifyIdentity.reviewPhoto.failTooltip.upload.${key}`);
	} else if (reason === "ai" && key !== "signature") {
		return i18nPackage.t(`beforeQuiz.verifyIdentity.reviewPhoto.failTooltip.ai.${key}`);
	}
	return null;
}

function TypedSignaturePhoto({ typedSignature }: { typedSignature: string }) {
	return (
		<div className={styles.typedSignatureContainer}>
			<Typography>{typedSignature}</Typography>
		</div>
	);
}

export const VerifyIdentityStep = forwardRef<
	HTMLDivElement,
	{ children: React.ReactNode; substeps: VerifyIdentityKey[] }
>(function VerifyIdentityStep({ children, substeps }, ref) {
	const hasSignature = substeps.includes("signature");
	const hasIdCheck = substeps.includes("idCard");
	const descriptionKey =
		hasSignature && hasIdCheck ? "description" : hasSignature ? "signatureOnlyDescription" : "idOnlyDescription";
	return (
		<BasicStep description={i18nPackage.t(`beforeQuiz.verifyIdentity.${descriptionKey}`)} ref={ref}>
			{children}
		</BasicStep>
	);
});

export default VerifyIdentityStep;
